import { StorageKey } from '../enum/StorageKey';

import { dateHasExpired } from './date';
import { generateCodeChallenge, generateCodeVerifier } from './pkce';

import {
  clearLocalStorage,
  getItemFromLocalStorage,
  removeItemFromLocalStorage,
  setItemToLocalStorage,
} from './local-storage';
import { clearSessionStorage } from './session-storage';

import { buildAuthorizeUrl, buildLogoutUrl } from '../service/auth-api';
import { TokenResponse } from '../interface/response/TokenResponse';
import { OAUTH_STATE_BYTE_LENGTH, SESSION_STORAGE_KEY } from '../constant/auth-constants';
import { ACCESS_TOKEN_CHANGED_EVENT } from '../constant/constants';

// ---------------------------------------------------------
// Session helpers (localStorage – persists across tabs/refresh)
// ---------------------------------------------------------

export const isLoggedIn = (): boolean => {
  const accessToken = getItemFromLocalStorage(StorageKey.ACCESS_TOKEN)?.trim();
  const expiryDate = getItemFromLocalStorage(StorageKey.EXPIRY_DATE)?.trim();

  return !!accessToken && !!expiryDate && !dateHasExpired(expiryDate);
};

export const getAccessToken = (): string | null => getItemFromLocalStorage(StorageKey.ACCESS_TOKEN)?.trim() || null;

export const getRefreshToken = (): string | null => getItemFromLocalStorage(StorageKey.REFRESH_TOKEN)?.trim() || null;

export const getIdToken = (): string | null => getItemFromLocalStorage(StorageKey.ID_TOKEN)?.trim() || null;

/**
 * Persist a token endpoint response into localStorage.
 * Computes EXPIRY_DATE from `expires_in` (seconds).
 */
export const persistTokenResponse = (tokens: TokenResponse): void => {
  setItemToLocalStorage(StorageKey.ACCESS_TOKEN, tokens.access_token);

  if (tokens.refresh_token) {
    setItemToLocalStorage(StorageKey.REFRESH_TOKEN, tokens.refresh_token);
  }

  if (tokens.id_token) {
    setItemToLocalStorage(StorageKey.ID_TOKEN, tokens.id_token);
  }

  const expiryDate = new Date(Date.now() + tokens.expires_in * 1000).toUTCString();
  setItemToLocalStorage(StorageKey.EXPIRY_DATE, expiryDate);

  window.dispatchEvent(new Event(ACCESS_TOKEN_CHANGED_EVENT));
};

/**
 * Removes only the OAuth/OIDC token-related keys from localStorage.
 *
 * Use this when you want to invalidate the user's auth state without
 * wiping unrelated data (e.g. cached `config`, `username`, UI prefs).
 *
 * For a full client-side wipe (logout), use `clearAllClientCache`.
 */
export const clearAuthTokens = (): void => {
  removeItemFromLocalStorage(StorageKey.ACCESS_TOKEN);
  removeItemFromLocalStorage(StorageKey.REFRESH_TOKEN);
  removeItemFromLocalStorage(StorageKey.ID_TOKEN);
  removeItemFromLocalStorage(StorageKey.EXPIRY_DATE);
  removeItemFromLocalStorage(StorageKey.PKCE_VERIFIER);
  removeItemFromLocalStorage(StorageKey.OAUTH_STATE);
};

/**
 * Wipe absolutely everything client-side so the next login starts from a
 * pristine state: localStorage, sessionStorage, and (best-effort) cookies
 * scoped to the current origin.
 */
export const clearAllClientCache = (): void => {
  try {
    clearLocalStorage();
  } catch (e) {
    console.warn('Failed to clear localStorage', e);
  }

  try {
    clearSessionStorage();
  } catch (e) {
    console.warn('Failed to clear sessionStorage', e);
  }

  // Best-effort cookie wipe (only non-HttpOnly cookies on the current path).
  try {
    document.cookie.split(';').forEach((c) => {
      const name = c.split('=')[0].trim();
      if (!name) return;
      const expire = 'expires=Thu, 01 Jan 1970 00:00:00 GMT';
      document.cookie = `${name}=; ${expire}; path=/`;
      document.cookie = `${name}=; ${expire}; path=/; domain=${window.location.hostname}`;
    });
  } catch (e) {
    console.warn('Failed to clear cookies', e);
  }
};

export const clearClientCacheAndReturnHome = (): void => {
  clearAllClientCache();
  window.location.replace('/');
};

// ---------------------------------------------------------
// PKCE login flow
// ---------------------------------------------------------

const generateState = (): string => {
  const arr = new Uint8Array(OAUTH_STATE_BYTE_LENGTH);
  window.crypto.getRandomValues(arr);
  return Array.from(arr, (b) => b.toString(OAUTH_STATE_BYTE_LENGTH).padStart(2, '0')).join('');
};

export async function startLogin(): Promise<void> {
  const verifier = generateCodeVerifier();
  const state = generateState();

  sessionStorage.removeItem(SESSION_STORAGE_KEY.CALLBACK_CODE);

  setItemToLocalStorage(StorageKey.PKCE_VERIFIER, verifier);
  setItemToLocalStorage(StorageKey.OAUTH_STATE, state);

  const challenge = await generateCodeChallenge(verifier);

  window.location.href = buildAuthorizeUrl({ codeChallenge: challenge, state });
}

export const logout = (): void => {
  const idToken = getIdToken() ?? undefined;

  // Nuke ALL client-side cache so the next login is pristine.
  clearAllClientCache();

  // Redirect to AuthProvider end-session so the IdP cookie / session is also
  // cleared. After that, AuthProvider redirects back to post_logout_redirect_uri.
  window.location.href = buildLogoutUrl(idToken);
};
